import { Image } from "expo-image";
import { Link } from "expo-router";
import { Linking, Platform, ScrollView, StyleSheet, View } from "react-native";

import { GText } from "@/components/g-text";

const TEXT_COLOR = "#FEF7F3";

export default function PrivacyPage() {
  if (Platform.OS !== "web") {
    return (
      <View style={styles.nativeContainer}>
        <GText style={styles.nativeText}>privacy policy</GText>
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      style={styles.container}
    >
      <View style={styles.main}>
        <View style={styles.logoContainer}>
          <Link href="/">
            <Image
              contentFit="contain"
              source={require("../../assets/logo.svg")}
              style={styles.logo}
            />
          </Link>
        </View>

        <GText style={styles.h1}>privacy</GText>

        <GText style={styles.paragraph}>
          at aglow, we value your privacy and are committed to protecting your
          personal information. this privacy policy outlines how we collect,
          use, share, and protect your information when you use our social media
          app.
        </GText>

        <View style={styles.consentNotice}>
          <GText style={styles.h2}>sms/text messaging consent</GText>
          <GText style={styles.paragraph}>
            by providing your phone number and opting in to text messages on
            aglow, you consent to receive recurring sms/text messages from
            aglow. these messages may include:
          </GText>
          <View style={styles.list}>
            <GText style={styles.listItem}>
              {"\u2022"} event reminders, updates, and invitations
            </GText>
            <GText style={styles.listItem}>
              {"\u2022"} account notifications and security alerts
            </GText>
            <GText style={styles.listItem}>
              {"\u2022"} promotional messages about aglow features and events
            </GText>
          </View>
          <GText style={styles.paragraph}>
            message frequency varies. message and data rates may apply. consent
            is not a condition of purchase or use of the app.
          </GText>
          <GText style={styles.paragraph}>
            you can opt out at any time by replying STOP to any message. for
            help, reply HELP or contact us at{" "}
            <GText
              onPress={() => Linking.openURL("mailto:hello@aglow.life")}
              style={styles.link}
            >
              hello@aglow.life
            </GText>
            . carriers are not liable for delayed or undelivered messages.
          </GText>
          <GText style={styles.paragraph}>
            we will not share your phone number or opt-in data with third
            parties or affiliates for their marketing purposes.
          </GText>
        </View>

        <GText style={styles.h2}>1. information we collect</GText>
        <GText style={styles.paragraph}>
          we collect various types of information to provide and improve our
          services, including:
        </GText>
        <View style={styles.list}>
          <GText style={styles.listItem}>
            {"\u2022"} personal information: when you sign up, we may collect
            information like your name, email address, phone number, and profile
            photo.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} content and activity: we collect the content you create
            and share on aglow, such as posts, comments, and interactions with
            other users.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} usage data: we gather information about how you use the
            app, including features accessed, settings chosen, and other
            engagement metrics.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} device information: we may collect details about your
            device, including ip address, operating system, and device model, to
            help improve performance and security.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} location data: with your permission, we may access your
            location to provide location-based features.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} event information: when you create, rsvp to, or interact
            with events on aglow, we collect details such as event preferences,
            attendance history, and related interactions.
          </GText>
        </View>

        <GText style={styles.h2}>2. how we use your information</GText>
        <GText style={styles.paragraph}>aglow uses your information to:</GText>
        <View style={styles.list}>
          <GText style={styles.listItem}>
            {"\u2022"} provide and personalize our services based on your
            preferences and activities.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} communicate with you about updates, new features, and
            promotional offers, including via sms/text messages.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} send you event-related notifications, reminders, updates,
            and invitations.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} analyze usage trends to improve app functionality and
            user experience.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} detect, prevent, and address security or technical
            issues.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} enforce our terms of service and protect the safety of
            our users and community.
          </GText>
        </View>

        <GText style={styles.h2}>3. events</GText>
        <GText style={styles.paragraph}>
          aglow allows you to discover, create, and participate in events. when
          you interact with events on aglow, we may use your information to:
        </GText>
        <View style={styles.list}>
          <GText style={styles.listItem}>
            {"\u2022"} recommend events based on your interests, location, and
            activity.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} share relevant details with event organizers (such as
            your name and profile information) when you rsvp or attend an event.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} send you reminders, updates, and follow-ups related to
            events you have expressed interest in or attended.
          </GText>
        </View>
        <GText style={styles.paragraph}>
          event organizers may have access to attendee information for their
          specific events. aglow is not responsible for how event organizers use
          your information outside of the app.
        </GText>

        <GText style={styles.h2}>4. sms/text messaging</GText>
        <GText style={styles.paragraph}>
          by providing your phone number and opting in, you consent to receive
          sms/text messages from aglow. these messages may include:
        </GText>
        <View style={styles.list}>
          <GText style={styles.listItem}>
            {"\u2022"} event reminders, updates, and invitations.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} account notifications and security alerts.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} promotional messages about aglow features and events.
          </GText>
        </View>
        <GText style={styles.paragraph}>
          message and data rates may apply. message frequency varies. you can
          opt out of sms messages at any time by replying &quot;stop&quot; to
          any message or by updating your notification preferences in the app.
          for help, reply &quot;help&quot; or contact us at{" "}
          <GText
            onPress={() => Linking.openURL("mailto:hello@aglow.life")}
            style={styles.link}
          >
            hello@aglow.life
          </GText>
          .
        </GText>
        <GText style={styles.paragraph}>
          we will not share your phone number with third parties for their
          marketing purposes. your phone number will only be used for the
          purposes described in this policy.
        </GText>

        <GText style={styles.h2}>
          5. anonymization and non-sale of contact information
        </GText>
        <GText style={styles.paragraph}>
          to safeguard your privacy, any contact information you share with us
          is anonymized for security purposes. we do not sell your personal
          information, including anonymized contact details, to third parties.
        </GText>

        <GText style={styles.h2}>6. information sharing and disclosure</GText>
        <GText style={styles.paragraph}>
          we do not sell your personal information to third parties. however, we
          may share your information in the following cases:
        </GText>
        <View style={styles.list}>
          <GText style={styles.listItem}>
            {"\u2022"} with your consent: we may share your information with
            third parties if you have given your explicit consent.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} service providers: we work with third-party vendors who
            help us operate, maintain, and improve the app. these providers have
            limited access to your information and are bound by privacy
            agreements.
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} legal requirements: we may disclose your information if
            required by law or in response to valid requests by public
            authorities (e.g., a court or government agency).
          </GText>
          <GText style={styles.listItem}>
            {"\u2022"} business transfers: if aglow is involved in a merger,
            acquisition, or asset sale, your information may be transferred as
            part of the transaction.
          </GText>
        </View>

        <GText style={styles.h2}>7. data security</GText>
        <GText style={styles.paragraph}>
          we take reasonable measures to protect your data from unauthorized
          access, alteration, or disclosure. however, no method of electronic
          storage or transmission is completely secure, and we cannot guarantee
          absolute security.
        </GText>

        <GText style={styles.h2}>8. data retention</GText>
        <GText style={styles.paragraph}>
          we retain your personal data only as long as necessary for the
          purposes set out in this privacy policy or as required by law. when no
          longer needed, your data will be securely deleted or anonymized.
        </GText>

        <GText style={styles.h2}>{"9. children's privacy"}</GText>
        <GText style={styles.paragraph}>
          aglow is not intended for users under the age of 13. we do not
          knowingly collect personal information from children. if we learn that
          we have collected information from a child under 13, we will take
          steps to delete it promptly.
        </GText>

        <GText style={styles.h2}>10. changes to this privacy policy</GText>
        <GText style={styles.paragraph}>
          we may update our privacy policy periodically to reflect changes in
          our practices or legal requirements. we will notify you of significant
          updates through the app or by email. continued use of aglow after
          updates constitutes acceptance of the revised policy.
        </GText>

        <GText style={styles.h2}>11. contact us</GText>
        <GText style={styles.paragraph}>
          if you have any questions about this privacy policy, please contact us
          at{" "}
          <GText
            onPress={() => Linking.openURL("mailto:hello@aglow.life")}
            style={styles.link}
          >
            hello@aglow.life
          </GText>
        </GText>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  consentNotice: {
    borderColor: "rgba(255,255,255,0.3)",
    borderRadius: 10,
    borderWidth: 1,
    marginBottom: 24,
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  container: {
    flex: 1,
  },
  h1: {
    color: TEXT_COLOR,
    fontSize: 32,
    fontWeight: "700",
    marginBottom: 24,
    textAlign: "center",
  },
  h2: {
    color: TEXT_COLOR,
    fontSize: 21,
    fontWeight: "600",
    marginBottom: 8,
    marginTop: 24,
    opacity: 0.9,
  },
  link: {
    color: TEXT_COLOR,
    textDecorationLine: "underline",
  },
  list: {
    marginBottom: 12,
    paddingLeft: 24,
  },
  listItem: {
    color: TEXT_COLOR,
    lineHeight: 24,
    marginBottom: 4,
    opacity: 0.85,
  },
  logo: {
    height: 30,
    width: 120,
  },
  logoContainer: {
    alignItems: "center",
    marginBottom: 24,
  },
  main: {
    alignSelf: "center",
    maxWidth: 700,
    paddingHorizontal: 16,
    paddingVertical: 32,
    width: "100%",
  },
  nativeContainer: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  nativeText: {
    color: TEXT_COLOR,
    fontSize: 24,
  },
  paragraph: {
    color: TEXT_COLOR,
    fontSize: 16,
    lineHeight: 26,
    marginBottom: 12,
    opacity: 0.85,
  },
  scrollContent: {
    flexGrow: 1,
  },
});
